import { useState, useMemo, useRef, useCallback, useEffect, useLayoutEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useIntercom } from "react-use-intercom";

import renderStakeCheckingPending from "../../Organisms/Modals/Stake Checking Status/stake-checking-pending";
import renderStakeCheckingVerificationPending from "../../Organisms/Modals/Stake Checking Status/stake-checking-verification-pending";
import renderStakeCheckingOnboardingStep from "../../Organisms/Modals/Stake Checking Status/stake-checking-onboarding-step";
import renderStakeCheckingDenied from "../../Organisms/Modals/Stake Checking Status/stake-checking-denied";
import renderCreditBuilderOptIn from "../../Organisms/Modals/Credit-Builder/credit-builder-opt-in";

import { useModalContext } from "../../Contexts/ModalContext";

import {
  isSVDApproved,
  isSVDEligible,
  isSVDPending,
  isSVDPendingVerification,
  isSVDReview,
  isSVDDenied,
  isSVDIneligible,
  hasSVDPhysicalCard,
  hasSVDVirtualCard,
  getMigratingBankStatusFromStakeDebitResponse,
  getBankMigrationNextStepsCardFromTrackerVariant,
  getBankMigrationTrackerVariantFromMigratingBankStatus,
} from "../../Utils/helpers";

const CARD_REQUEST_CARDS = {
  "connect-bank": {
    title: "Request your virtual card",
    subtitle: "Connect your bank and add cash to your account to request your card.",
    cta: "Connect your bank",
  },
  "add-cash": {
    title: "Request your virtual card",
    subtitle: "Add any amount to your account to request your card.",
    cta: "Add cash",
  },
  // No CTA — the card clears itself once the transfer settles
  "transfer-pending": {
    title: "Request your virtual card",
    subtitle: "Once your pending transfer clears, you can request your card.",
  },
  "request-virtual": {
    title: "Request your virtual card",
    subtitle: "Your account is funded. Request your card to start spending.",
    cta: "Request card",
  },
  "request-physical": {
    title: "Request your physical card",
    subtitle: "Free shipping to your address. Arrives in 7 to 10 business days.",
    cta: "Request card",
  },
};

function readCardGapPx() {
  const raw = getComputedStyle(document.documentElement).getPropertyValue("--card-gap").trim();
  const n = parseFloat(raw);
  return Number.isFinite(n) ? n : 12;
}

function BankMigrationNextStepsCard({ title, subtitle, theme, onClick }) {
  return (
    <div className={`ns-card ns-hero ns-hero--migration ${theme}`} onClick={onClick}>
      <div className="ns-body">
        <div className="ns-title">{title}</div>
        <div className="ns-sub">{subtitle}</div>
      </div>
      <span className="ns-chevron-badge" aria-hidden>
        <svg className="ns-chevron" width="16" height="16" viewBox="0 0 16 16" fill="none">
          <path d="M6 4l4 4-4 4" stroke="#0F1219" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </span>
    </div>
  );
}

function NextStepsCard({ type, lockReason, onClick }) {
  const chevron = (
    <svg className="ns-chevron" width="16" height="16" viewBox="0 0 16 16" fill="none">
      <path d="M6 4l4 4-4 4" stroke="#0F1219" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
  const smallChevron = (
    <svg className="ns-chevron" width="16" height="16" viewBox="0 0 16 16" fill="none">
      <path d="M6 4l4 4-4 4" stroke="#0F1219" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
  const lockIcon = (
    <svg className="ns-lock-icon" width="16" height="16" viewBox="0 0 16 16" fill="none">
      <rect x="4" y="8" width="8" height="6" rx="1.5" stroke="#8B8FA3" strokeWidth="1.3" />
      <path d="M6 8V6a2 2 0 014 0v2" stroke="#8B8FA3" strokeWidth="1.3" strokeLinecap="round" />
    </svg>
  );

  if (type === "apply") {
    return (
      <div className="ns-card ns-hero green" onClick={onClick}>
        <div className="ns-body">
          <div className="ns-title">Set up Stake Debit</div>
          <div className="ns-sub">
            Earn up to $120/year cash back on rent, plus extra cash back on qualifying purchases
          </div>
        </div>
        {chevron}
      </div>
    );
  }

  if (type === "continue" ) {
    return (
      <div className="ns-card ns-hero green" onClick={onClick}>
        <div className="ns-body">
          <div className="ns-title">Finish setting up Stake Debit</div>
          <div className="ns-sub">Pick up where you left off</div>
        </div>
        {chevron}
      </div>
    );
  }

  if (type === "denied") {
    return (
      <div className="ns-card ns-hero muted" onClick={onClick}>
        <div className="ns-body" style={{ gap: "4px" }}>
          <div className="ns-title">Application Denied</div>
          <div className="ns-sub">Unfortunately, your application could not be approved at this time</div>
        </div>
      </div>
    );
  }

  if (type === "ineligible") {
    return (
      <div className="ns-card ns-hero muted">
        <div className="ns-body" style={{ gap: "4px" }}>
          <div className="ns-title">Stake Checking Unavailable</div>
          <div className="ns-sub">You're not currently eligible for Stake Checking</div>
        </div>
      </div>
    );
  }

  if (type === "under-review") {
    return (
      <div className="ns-card ns-hero green" onClick={onClick}>
        <div className="ns-body" style={{ gap: "4px" }}>
          <div className="ns-title">Application Under Review</div>
          <div className="ns-sub">We're reviewing your information — usually takes 1–2 business days</div>
          <div className="ns-status">
            <span className="ns-status-dot" />
            <span className="ns-status-text">Processing</span>
          </div>
        </div>
      </div>
    );
  }

  if (type === "verification") {
    return (
      <div className="ns-card ns-hero green" onClick={onClick}>
        <div className="ns-body" style={{ gap: "4px" }}>
          <div className="ns-title">Additional Verification Required</div>
          <div className="ns-sub">We need a few more details to verify your identity.</div>
        </div>
      </div>
    );
  }

  if (type === "pending") {
    return (
      <div className="ns-card ns-hero green" onClick={onClick}>
        <div className="ns-body" style={{ gap: "4px" }}>
          <div className="ns-title">Application Pending</div>
          <div className="ns-sub">Your application is being processed. We'll notify you once there's an update.</div>
          <div className="ns-status">
            <span className="ns-status-dot" />
            <span className="ns-status-text">Processing</span>
          </div>
        </div>
      </div>
    );
  }

  if (type === "approved") {
    return (
      <div className="ns-card ns-hero green" onClick={onClick}>
        <div className="ns-body" style={{ gap: "4px" }}>
          <div className="ns-title">Application Approved</div>
          <div className="ns-sub">Your Stake Checking application has been approved</div>
          <div className="ns-status">
            <span className="ns-status-dot approved" />
            <span className="ns-status-text">Approved</span>
          </div>
        </div>
      </div>
    );
  }

  if (type === "debit-card-requested") {
    return (
      <div className="ns-card ns-hero green" onClick={onClick}>
        <div className="ns-body" style={{ gap: "4px" }}>
          <div className="ns-title">Activate Your Card</div>
          <div className="ns-sub">Your debit card has been requested and is on its way</div>
          <div className="ns-status">
            <span className="ns-status-dot" />
            <span className="ns-status-text">Card Requested</span>
          </div>
        </div>
      </div>
    );
  }

  const cardRequest = CARD_REQUEST_CARDS[type];
  if (cardRequest) {
    return (
      <div className="ns-card ns-hero green" onClick={onClick}>
        <div className="ns-body">
          <div className="ns-title">{cardRequest.title}</div>
          <div className="ns-sub">{cardRequest.subtitle}</div>
          {!!cardRequest.cta && (
            <div className="ns-status">
              <span className="ns-status-text">{cardRequest.cta} &gt;</span>
            </div>
          )}
        </div>
        {!!cardRequest.cta && chevron}
      </div>
    );
  }

  if (type === "dd-apply") {
    return (
      <div className="ns-card ns-hero blue" onClick={onClick}>
        <div className="ns-body">
          <div className="ns-title">Set up Direct Deposit</div>
          <div className="ns-sub">Connect your paycheck. Get paid up to 2 days early.</div>
        </div>
        {chevron}
      </div>
    );
  }

  if (type === "dd-locked") {
    return (
      <div className="ns-card ns-hero locked">
        <div className="ns-body">
          <div className="ns-title">Set up Direct Deposit</div>
          <div className="ns-sub">Get paid up to 2 days early. Set up Stake Debit to unlock.</div>
        </div>
        {lockIcon}
      </div>
    );
  }

  if (type === "cb-opt-in") {
    return (
      <div className="ns-card ns-compact purple" onClick={onClick}>
        <div className="ns-body">
          <div className="ns-title">Opt in to Credit Builder</div>
          <div className="ns-sub">Build credit by reporting rent payments. Free.</div>
        </div>
        {smallChevron}
      </div>
    );
  }


  return null;
}

export default function NextSteps({
  stakeDebit = {},
  payrollAccountsData = {},
  creditReporting = {},
  connectedAccountsData = {},
  activitiesData = {},
  checkingStep = null,
}) {
  const navigate = useNavigate();
  const modalContext = useModalContext();
  const { show: showIntercom } = useIntercom();

  const [nsSlide, setNsSlide] = useState(0);
  const [nsStepPx, setNsStepPx] = useState(0);
  const [nsDragging, setNsDragging] = useState(false);
  const nsSliderRef = useRef(null);
  const nsWrapperRef = useRef(null);
  const nsTrackRef = useRef(null);
  const nsDragRef = useRef({ dragging: false, startX: 0, deltaX: 0 });
  const nsCountRef = useRef(0);

  useLayoutEffect(() => {
    const el = nsWrapperRef.current;
    if (!el) return;

    const measure = () => {
      const w = el.offsetWidth;
      if (w <= 0) return;
      setNsStepPx(w + readCardGapPx());
    };

    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const nsGoTo = useCallback((i) => setNsSlide(Math.max(0, Math.min(i, nsCountRef.current - 1))), []);

  const onNsPointerDown = useCallback((e) => {
    nsDragRef.current = { dragging: true, startX: e.clientX, deltaX: 0, captured: false, pointerId: e.pointerId };
  }, []);

  const onNsPointerMove = useCallback(
    (e) => {
      if (!nsDragRef.current.dragging) return;
      nsDragRef.current.deltaX = e.clientX - nsDragRef.current.startX;
      if (!nsDragRef.current.captured && Math.abs(nsDragRef.current.deltaX) > 5) {
        nsDragRef.current.captured = true;
        setNsDragging(true);
        try {
          nsSliderRef.current?.setPointerCapture(nsDragRef.current.pointerId);
        } catch (_) {}
      }
      if (nsDragRef.current.captured && nsTrackRef.current && nsWrapperRef.current) {
        const gap = readCardGapPx();
        const vw = nsWrapperRef.current.offsetWidth;
        const step = vw + gap;
        const offsetPx = -nsSlide * step + nsDragRef.current.deltaX;
        nsTrackRef.current.style.transform = `translateX(${offsetPx}px)`;
      }
    },
    [nsSlide],
  );

  const onNsPointerUp = useCallback(() => {
    if (!nsDragRef.current.dragging) return;
    nsDragRef.current.dragging = false;
    setNsDragging(false);
    if (nsDragRef.current.captured) {
      const threshold = (nsWrapperRef.current?.offsetWidth || 300) * 0.2;
      if (nsDragRef.current.deltaX < -threshold) nsGoTo(nsSlide + 1);
      else if (nsDragRef.current.deltaX > threshold) nsGoTo(nsSlide - 1);
      else nsGoTo(nsSlide);
      // Clear the inline transform so React's style prop takes over with transition
      if (nsTrackRef.current) nsTrackRef.current.style.transform = "";
    }
  }, [nsSlide, nsGoTo]);

  const svdData = stakeDebit?.data;
  const isApproved = isSVDApproved(svdData);
  const hasPayroll = (payrollAccountsData?.data?.data?.accounts?.length ?? 0) > 0;
  const isBankConnected = (connectedAccountsData?.data?.data?.accounts?.length ?? 0) > 0;
  const availableBalance = svdData?.data?.available ?? 0;
  const isCreditBuilderOptedIn = creditReporting?.data?.data?.credit_builder?.opted_in;
  const isCreditBuilderEligible = creditReporting?.data?.data?.credit_builder?.is_eligible;
  const hasPendingTransaction = activitiesData?.data?.data?.hasPendingTransaction ?? false;
  const nextStepCards = useMemo(() => {
    const cards = [];
    const migrationTrackerVariant = getBankMigrationTrackerVariantFromMigratingBankStatus(
      getMigratingBankStatusFromStakeDebitResponse(stakeDebit),
    );
    const openMigrationStatus = () => navigate("/bank-migration-status");
    const openCheckingModal = () => renderStakeCheckingOnboardingStep(modalContext);
    const openCheckingFlow = migrationTrackerVariant ? openMigrationStatus : openCheckingModal;

    // 1. Checking card — migration tracker from stake_debit, then Next Steps Matrix
    const migrationCard = getBankMigrationNextStepsCardFromTrackerVariant(migrationTrackerVariant);
    if (migrationCard) {
      const handleMigrationCardClick = migrationCard.openSupport ? showIntercom : openMigrationStatus;

      cards.push(
        <BankMigrationNextStepsCard
          key="checking"
          title={migrationCard.title}
          subtitle={migrationCard.subtitle}
          theme={migrationCard.theme}
          onClick={handleMigrationCardClick}
        />,
      );
    } else if (isSVDEligible(svdData) && checkingStep !== "checking-name") {
      // Dropped at a specific onboarding step → open step-specific modal
      cards.push(
        <NextStepsCard
          key="checking"
          type="continue"
          onClick={openCheckingFlow}
        />,
      );
    } else if (isApproved && !hasSVDVirtualCard(stakeDebit)) {
      // Approved but no virtual card yet → fund the account, then request the card
      let virtualCardType;
      // A funded account gets its card auto-issued via webhook, but that can lag, so
      // surface it as an actionable request rather than a silent wait.
      if (availableBalance > 0) virtualCardType = "request-virtual";
      else if (hasPendingTransaction) virtualCardType = "transfer-pending";
      else if (!isBankConnected) virtualCardType = "connect-bank";
      else virtualCardType = "add-cash";

      // transfer-pending is passive — it resolves on its own when the transfer settles
      const openVirtualCardFlow =
        virtualCardType === "transfer-pending" ? undefined : openCheckingModal;

      cards.push(<NextStepsCard key="checking" type={virtualCardType} onClick={openVirtualCardFlow} />);
    } else if (isApproved && !hasSVDPhysicalCard(svdData)) {
      // Virtual card is set up → offer the physical one
      cards.push(
        <NextStepsCard key="checking" type="request-physical" onClick={openCheckingModal} />,
      );
    } else if (isSVDEligible(svdData)) {
      // Not started → open wizard at step 0
      cards.push(
        <NextStepsCard key="checking" type="apply" onClick={openCheckingFlow} />,
      );
    } else if (isSVDReview(svdData)) {
      // Under Review → passive waiting state, no modal
      cards.push(<NextStepsCard key="checking" type="under-review" />);
    } else if (isSVDPendingVerification(svdData)) {
      cards.push(<NextStepsCard key="checking" type="verification" onClick={() => renderStakeCheckingVerificationPending(modalContext)} />);
    } else if (isSVDPending(svdData)) {
      // Generic pending → pending modal
      cards.push(
        <NextStepsCard key="checking" type="pending" onClick={() => renderStakeCheckingPending(modalContext)} />,
      );
    } else if (isSVDDenied(svdData)) {
      // Denied → terminal state, show denied modal
      cards.push(
        <NextStepsCard key="checking" type="denied" onClick={() => renderStakeCheckingDenied(modalContext)} />,
      );
    } else if (isSVDIneligible(svdData)) {
      // Ineligible → no modal
      cards.push(<NextStepsCard key="checking" type="ineligible" />);
    } else if (isSVDApproved(svdData) && svdData?.data?.status === "DebitCardRequested") {
      // Debit card requested → inline activation, no modal
      cards.push(
        <NextStepsCard
          key="checking"
          type="debit-card-requested"
          onClick={() => navigate("/manage-stake-debit")}
        />,
      );
    }

    // 2. Direct Deposit card
    if (isApproved && !hasPayroll) {
      cards.push(
        <NextStepsCard key="dd" type="dd-apply" onClick={() => navigate("/direct-deposit")} />,
      );
    } else if (!isApproved && isSVDEligible(svdData)) {
      cards.push(<NextStepsCard key="dd-locked" type="dd-locked" />);
    }

    // 3. Credit Builder card
    if (isCreditBuilderEligible && !isCreditBuilderOptedIn) {
      cards.push(
        <NextStepsCard key="cb" type="cb-opt-in" onClick={() => renderCreditBuilderOptIn(modalContext)} />,
      );
    }

    return cards;
  }, [svdData, isApproved, stakeDebit, hasPayroll, isCreditBuilderOptedIn, isCreditBuilderEligible, modalContext, navigate, checkingStep, showIntercom, isBankConnected, availableBalance, hasPendingTransaction]);

  nsCountRef.current = nextStepCards.length;

  useEffect(() => {
    setNsSlide((prev) => (prev >= nextStepCards.length ? 0 : prev));
  }, [nextStepCards.length]);

  if (nextStepCards.length === 0) return null;

  return (
    <div
      className="next-steps"
      ref={nsSliderRef}
      onPointerDown={onNsPointerDown}
      onPointerMove={onNsPointerMove}
      onPointerUp={onNsPointerUp}
      onPointerCancel={onNsPointerUp}
    >
      <div className="ns-track-wrapper" ref={nsWrapperRef}>
        <div
          className={`ns-track${nsDragging ? " dragging" : ""}`}
          ref={nsTrackRef}
          style={
            nsStepPx > 0 ? { transform: `translateX(-${nsSlide * nsStepPx}px)` } : { transform: "translateX(0)" }
          }
        >
          {nextStepCards.map((card, i) => (
            <div key={i} className="ns-slide">
              {card}
            </div>
          ))}
        </div>
      </div>
      {nextStepCards.length > 1 && (
        <div className="ns-dots" role="tablist" aria-label="Next steps slides">
          {nextStepCards.map((_, i) => (
            <button
              key={i}
              type="button"
              className={`ns-dot${nsSlide === i ? " active" : ""}`}
              onClick={() => nsGoTo(i)}
              aria-label={`Go to next step ${i + 1} of ${nextStepCards.length}`}
              aria-current={nsSlide === i ? "true" : undefined}
            />
          ))}
        </div>
      )}
    </div>
  );
}
